import { appConfirm } from "@/lib/appDialog";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { toast } from "sonner";
import { Plus, BookOpen, Pencil, Trash2, Loader2, Users, Eye } from "lucide-react";

export default function CoursesList() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", cover_image_url: "", program_name: "", status: "draft" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/courses");
      setCourses(data.courses || []);
    } catch (e) {
      setError(formatApiError(e));
      toast.error(formatApiError(e));
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) { toast.error("الاسم مطلوب"); return; }
    setSaving(true);
    try {
      await api.post("/courses", form);
      toast.success("تم إنشاء المقرر");
      setShowForm(false);
      setForm({ title: "", description: "", cover_image_url: "", program_name: "", status: "draft" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!await appConfirm("هل تريد حذف المقرر مع كل محتواه؟")) return;
    try {
      await api.delete(`/courses/${id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout>
      <div data-testid="courses-list">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">وحدة التعلّم</div>
            <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">المقررات التدريبية</h1>
            <p className="mt-2 text-slate-600">أنشئ مقررات وأقسام ومواد تعليمية واختبارات آلية للطلاب.</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 transition-colors"
            data-testid="new-course-btn"
          >
            <Plus className="h-5 w-5" /> مقرر جديد
          </button>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="courses-list-error" />
        ) : courses.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="لا توجد مقررات بعد"
            description="ابدأ بإنشاء مقررك الأول لإثراء المنصة بالمحتوى."
            testid="courses-list-empty"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {courses.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:card-shadow-lg hover:border-teal-200 transition-all group" data-testid={`course-${c.id}`}>
                <div className="h-40 bg-slate-100 relative overflow-hidden">
                  {c.cover_image_url ? (
                    <img src={c.cover_image_url} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-teal-600 to-teal-800 grid place-items-center">
                      <BookOpen className="h-14 w-14 text-white/60" strokeWidth={1.5} />
                    </div>
                  )}
                  <span className={`absolute top-3 start-3 px-2.5 py-1 rounded-full text-xs font-semibold ${
                    c.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-700"
                  }`}>
                    {c.status === "published" ? "منشور" : "مسودة"}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-bold text-lg text-slate-900 line-clamp-1">{c.title}</h3>
                  {c.program_name && <div className="text-xs text-teal-700 font-medium mt-1">{c.program_name}</div>}
                  <p className="text-sm text-slate-600 mt-2 line-clamp-2">{c.description || "لا يوجد وصف"}</p>
                  <div className="mt-5 flex items-center justify-between pt-4 border-t border-slate-100">
                    <Link to={`/dashboard/courses/${c.id}`} className="inline-flex items-center gap-1.5 text-teal-700 text-sm font-semibold hover:gap-2 transition-all" data-testid={`open-course-${c.id}`}>
                      <Pencil className="h-4 w-4" /> إدارة المحتوى
                    </Link>
                    <button onClick={() => remove(c.id)} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-course-${c.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setShowForm(false)}>
            <div className="w-full max-w-lg bg-white rounded-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="course-form">
              <h3 className="text-2xl font-heading font-black text-slate-900 mb-6">مقرر جديد</h3>
              <div className="space-y-4">
                <Input label="عنوان المقرر" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="course-title" />
                <Input label="اسم البرنامج" value={form.program_name} onChange={(v) => setForm({ ...form, program_name: v })} testid="course-program" />
                <Input label="الوصف" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea rows={3} testid="course-desc" />
                <Input label="رابط صورة الغلاف" value={form.cover_image_url} onChange={(v) => setForm({ ...form, cover_image_url: v })} testid="course-cover" />
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">الحالة</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3">
                    <option value="draft">مسودة</option>
                    <option value="published">منشور</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={() => setShowForm(false)} className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">إلغاء</button>
                <button onClick={create} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="save-course-btn">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  إنشاء
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Input({ label, value, onChange, textarea, rows = 2, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      {textarea
        ? <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid={testid} />
        : <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid={testid} />}
    </div>
  );
}
